/**
 * Local pre-validation of the draft cart for UX feedback only.
 * The Cloud Functions remain the authoritative source — see
 * functions/src/lib/limits.ts (port of ProductDetail.js:246-341).
 */
import { Category, DraftCartLine, Product } from "./types";

export type LimitCode =
  | "product_max_quantity"
  | "product_round_quantity"
  | "category_max_quantity"
  | "global_max_quantity";

export interface PreValidateInput {
  customerNb: number;
  draftLines: DraftCartLine[];
  productsById: Map<string, Product>;
  categoriesById: Map<string, Category>;
  quantityHistory: Record<string, number>;
  categoryQuantityHistory: Record<string, number>;
  globalMaxQuantity?: number;
  /** À la carte : tout le panier compte dans le plafond global ; sinon seuls les gratuits. */
  aLaCarte?: boolean;
}

export const preValidate = (input: PreValidateInput): { code: LimitCode; productId?: string; categoryId?: string } | null => {
  const { customerNb, draftLines, productsById, categoriesById,
          quantityHistory, categoryQuantityHistory,
          globalMaxQuantity = 0, aLaCarte = false } = input;

  if (!draftLines.length || customerNb <= 0) return null;

  const draftByProduct = new Map<string, number>();
  const draftByCategory = new Map<string, number>();
  for (const l of draftLines) {
    draftByProduct.set(l.productId, (draftByProduct.get(l.productId) || 0) + l.qty);
    const cat = productsById.get(l.productId)?.categoryId;
    if (cat) draftByCategory.set(cat, (draftByCategory.get(cat) || 0) + l.qty);
  }

  for (const [productId, draftQty] of draftByProduct) {
    const product = productsById.get(productId);
    if (!product) continue;
    if (product.maxQuantity && product.maxQuantity > 0) {
      const historyQty = quantityHistory[productId] || 0;
      if (draftQty + historyQty > product.maxQuantity * customerNb) {
        return { code: "product_max_quantity", productId };
      }
    }
    if (product.roundQuantity && product.roundQuantity > 0) {
      if (draftQty > product.roundQuantity * customerNb) {
        return { code: "product_round_quantity", productId };
      }
    }
  }

  for (const [categoryId, draftQty] of draftByCategory) {
    const category = categoriesById.get(categoryId);
    if (!category?.maxQuantity || category.maxQuantity <= 0) continue;
    const historyQty = categoryQuantityHistory[categoryId] || 0;
    if (draftQty + historyQty > category.maxQuantity * customerNb) {
      return { code: "category_max_quantity", categoryId };
    }
  }

  // Plafond global par envoi (Restaurant.js:1044-1054) : en formule, seuls les
  // gratuits comptent. maxQuantityPerMenu est géré par useSubmitGate (cumul des
  // gratuits de orderHistory) et par le serveur.
  if (globalMaxQuantity > 0) {
    const counted = draftLines
      .filter((l) => aLaCarte || (l.unitPrice ?? 0) === 0)
      .reduce((s, l) => s + l.qty, 0);
    if (counted > globalMaxQuantity * customerNb) {
      return { code: "global_max_quantity" };
    }
  }

  return null;
};
